import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import MenuGroup from '@atlaskit/menu/menu-group';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import type { DevDocsBylineConnection } from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';
import { DevDocsBylineConnectionMenuSection } from '@confluence/dev-docs/entry-points/DevDocsBylineConnectionMenuSection.ts';

import {
	BylineConnectionIconStack,
	type BylineConnectionIconStackProps,
} from './BylineConnectionIconStack.tsx';
import { ConnectionsByLineExpandableLinkSection } from './ConnectionsByLineExpandableLinkSection.tsx';
import { ConnectionsByLineMenuFooter } from './footer/ConnectionsByLineMenuFooter.tsx';
import type { RefreshLinkUrlHandler } from './connectionTypes.ts';
import { useMenuViewAnalytics } from './hooks/useMenuViewAnalytics.ts';
import {
	getSectionHeadingValues,
	relatedContentHeadingMessage,
} from './utils/menuSectionHeading.ts';
import { selectBylineMenuLinks } from './utils/selectBylineMenuLinks.ts';
import type { EnhancedSmartLink } from './utils/smartLinkTypes.ts';
import { useTwgAnalyticsEvents } from './useTwgAnalyticsEvents.ts';

const MENU_WIDTH = '320px';
const TWG_MENU_BODY_MAX_HEIGHT = '600px';

const i18n = defineMessages({
	connectionsTwg: {
		id: 'byline-connections.menu.header.title-teamwork-graph-twg.do-not-translate',
		defaultMessage: 'Teamwork Graph',
		description:
			'Accessible name of the connections byline menu, which lists the links and references connected to the current page. "Teamwork Graph" is a product name and is intentionally not translated.',
	},
	viewMoreContext: {
		id: 'byline-connections.menu.view-more-context',
		defaultMessage: 'View more context',
		description:
			'Label for the button at the end of the connections byline menu that opens the Teamwork Graph preview panel for the current page.',
	},
	appearsIn: {
		id: 'byline-connections.menu.section.appears-in',
		defaultMessage: 'Appears in ({count}{isTruncated, select, true {+} other {}})',
		description:
			'Heading of the connections byline menu section listing the other pages and items that reference the current page, followed by the number of references listed in parentheses. A "+" follows the number when the menu lists only some of them and the rest are in the preview panel.',
	},
	linksTo: {
		id: 'byline-connections.menu.section.links-to',
		defaultMessage: 'Links to ({count}{isTruncated, select, true {+} other {}})',
		description:
			'Heading of the connections byline menu section listing the links found on the current page, followed by the number of links listed in parentheses. A "+" follows the number when the menu lists only some of them and the rest are in the preview panel.',
	},
});

const styles = cssMap({
	menu: {
		width: MENU_WIDTH,
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.medium'),
	},
	twgContent: {
		paddingBlockEnd: token('space.025'),
		maxHeight: TWG_MENU_BODY_MAX_HEIGHT,
		overflowY: 'auto',
	},
	viewMoreContext: {
		paddingInline: token('space.100'),
		paddingBlock: token('space.150'),
	},
	loading: {
		display: 'flex',
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		justifyContent: 'center',
	},
});

export type ConnectionsByLineMenuProps = Pick<
	BylineConnectionIconStackProps,
	'additionalIcons' | 'minIcons'
> & {
	showFooter?: boolean;
	links?: EnhancedSmartLink[];
	incomingLinks?: EnhancedSmartLink[];
	/** The graph has further Appears in pages beyond `incomingLinks`. */
	incomingLinksHaveMore?: boolean;
	/**
	 * Every Appears in source has answered (see `UseIncomingLinksResult.settled`). Until then the
	 * menu body shows a spinner in place of the sections: the row slots are filled in priority order,
	 * so a late Appears in row would also reshuffle Links to under the reader. Defaults to settled for
	 * callers that pass a fixed list.
	 */
	incomingLinksSettled?: boolean;
	relatedContentLinks?: EnhancedSmartLink[];
	/**
	 * Related content is still being fetched or hydrated. It takes the top row slots, so the spinner
	 * also stays up until it answers; otherwise a page that already qualifies on Links to shows those
	 * rows and then swaps them for Related content under the reader (CCINT-9391). Defaults to loaded
	 * for callers that pass a fixed list.
	 */
	relatedContentLoading?: boolean;
	devDocsConnection?: DevDocsBylineConnection | null;
	contentId?: string;
	refreshLinkUrl: RefreshLinkUrlHandler;
	onNavigate?: () => void;
	onOpenPreviewPanel: () => void;
};

export const ConnectionsByLineMenu = ({
	showFooter = true,
	links,
	incomingLinks,
	incomingLinksHaveMore = false,
	incomingLinksSettled = true,
	additionalIcons,
	minIcons,
	relatedContentLinks,
	relatedContentLoading = false,
	devDocsConnection,
	contentId,
	refreshLinkUrl,
	onNavigate,
	onOpenPreviewPanel,
}: ConnectionsByLineMenuProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	// The sections render only once every source that fills the shared row slots has answered.
	const sectionsSettled = incomingLinksSettled && !relatedContentLoading;
	// Related content leads; Appears in and Links to fill the remaining rows when it is thin.
	const menuLinks = useMemo(
		() =>
			selectBylineMenuLinks({
				relatedContentLinks,
				incomingLinks,
				outgoingLinks: links,
			}),
		[relatedContentLinks, incomingLinks, links],
	);
	const visibleLinks = useMemo(
		() => [...menuLinks.relatedContent, ...menuLinks.appearsIn, ...menuLinks.linksTo],
		[menuLinks],
	);
	const sectionCounts = useMemo(
		() => ({
			relatedContentCount: menuLinks.relatedContent.length,
			appearsInCount: menuLinks.appearsIn.length,
			linksToCount: menuLinks.linksTo.length,
		}),
		[menuLinks],
	);
	useMenuViewAnalytics({
		visibleLinks,
		ready: sectionsSettled,
		source: 'connectionsBylineMenu',
		sectionCounts,
	});

	const handleViewMoreContext = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'connectionsBylineViewMoreContext',
				source: 'connectionsBylineMenu',
			},
		}).fire();
		onOpenPreviewPanel();
	}, [createAnalyticsEvent, onOpenPreviewPanel]);

	return (
		<Box xcss={styles.menu} role="dialog" aria-label={formatMessage(i18n.connectionsTwg)}>
			<Stack>
				<Box xcss={styles.twgContent} testId="connections-byline-menu.content">
					{devDocsConnection ? (
						<MenuGroup>
							<DevDocsBylineConnectionMenuSection
								connection={devDocsConnection}
								onNavigate={onNavigate}
							/>
						</MenuGroup>
					) : null}
					{/* Rows stay compact and static; View more context opens the full preview panel. */}
					{!sectionsSettled ? (
						<Box xcss={styles.loading}>
							<Spinner size="medium" testId="connections-byline-menu.loading" />
						</Box>
					) : (
						<>
							<ConnectionsByLineExpandableLinkSection
								heading={formatMessage(
									relatedContentHeadingMessage,
									getSectionHeadingValues(
										menuLinks.relatedContent.length,
										relatedContentLinks?.length ?? 0,
									),
								)}
								links={menuLinks.relatedContent}
								refreshLinkUrl={refreshLinkUrl}
								linkDirection="incoming"
								onNavigate={onNavigate}
								compact
							/>
							<ConnectionsByLineExpandableLinkSection
								heading={formatMessage(
									i18n.appearsIn,
									getSectionHeadingValues(
										menuLinks.appearsIn.length,
										incomingLinks?.length ?? 0,
										incomingLinksHaveMore,
									),
								)}
								links={menuLinks.appearsIn}
								refreshLinkUrl={refreshLinkUrl}
								linkDirection="incoming"
								onNavigate={onNavigate}
								compact
							/>
							<ConnectionsByLineExpandableLinkSection
								heading={formatMessage(
									i18n.linksTo,
									getSectionHeadingValues(menuLinks.linksTo.length, links?.length ?? 0),
								)}
								links={menuLinks.linksTo}
								refreshLinkUrl={refreshLinkUrl}
								linkDirection="outgoing"
								onNavigate={onNavigate}
								compact
							/>
							{showFooter ? (
								<ConnectionsByLineMenuFooter links={links ?? []} contentId={contentId} />
							) : null}
							{/* Hidden with the sections so the loading state is only the spinner. */}
							<Box xcss={styles.viewMoreContext}>
								<Button
									appearance="subtle"
									iconBefore={TeamworkGraphIcon}
									iconAfter={ArrowRightIcon}
									shouldFitContainer
									onClick={handleViewMoreContext}
									testId="connections-byline-menu.view-more-context-button"
								>
									<Inline as="span" space="space.100" alignBlock="center">
										<Text weight="regular">{formatMessage(i18n.viewMoreContext)}</Text>
										<BylineConnectionIconStack
											links={links ?? []}
											incomingLinks={incomingLinks}
											relatedContentLinks={relatedContentLinks}
											additionalIcons={additionalIcons}
											minIcons={minIcons}
											spacing="none"
										/>
									</Inline>
								</Button>
							</Box>
						</>
					)}
				</Box>
			</Stack>
		</Box>
	);
};
